import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { AgentPermissionResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private.ts';

type UseAgentPermissions = {
	agentId: string;
	enabled: boolean;
} & AssistanceServiceInjectedParams;

export const useAgentPermissions: (
	props: Omit<UseAgentPermissions, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam,
) => {
	data: AgentPermissionResponse | null;
	error: ApiError | null;
	isFetching: boolean;
	refetch: () => Promise<AgentPermissionResponse | null>;
} = createHookWithAssistanceService(
	({
		agentId,
		enabled,
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
	}: UseAgentPermissions) => {
		const query = useQuery({
			queryKey: ['getAgentPermissions', agentId],
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.GET_AGENT_PERMISSIONS,
					name: ExperienceName.GET_AGENT_PERMISSIONS,
				});

				return await assistanceService.getAgentPermissions(agentId, {
					permission_ids: [
						'AGENT_CREATE',
						'AGENT_UPDATE',
						'AGENT_DELETE',
						'AGENT_DEACTIVATE',
						'AGENT_READ',
					],
				});
			},
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.GET_AGENT_PERMISSIONS,
				});
			},
			onError: (error) => {
				experienceTracker.fail({
					name: ExperienceName.GET_AGENT_PERMISSIONS,
					error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
			enabled,
		});

		return query;
	},
);
